import {
  ICharge,
  IChargeWithDisplayText,
  IRecurringCharge,
  IRecurringChargeWithBaseValue,
  IRecurringChargeWithDisplayText
} from './price';
import { PRODUCT_FAMILY } from './productFamily';

export { PRODUCT_FAMILY };

export type MobileProductTier = 'X_SMALL' | 'SMALL' | 'MEDIUM' | 'LARGE' | 'X_LARGE';
export type MobileDataProductTier = 'SMALL' | 'MEDIUM' | 'LARGE';

export interface IData {
  unit: string;
  value: number;
}

export interface INetworkSpeed {
  upstream: IData;
  downstream: IData;
}

// generic product code interface
// for nbn and mobile product
export interface IProductCode {
  /**
   * For NBN products, the `productCode` on the `IProductCode` is base plan product code, this will have the same
   * value for the base plan and any promos for that plan
   * eg. premium and premium 10 have the same `productCode` value
   *
   * NOTE: This is called `planProductCode` on the `INbnProduct`
   */
  productCode: string;
}

export interface IProduct extends IProductCode {
  productName: string;
  /**
   * For NBN products, the `productId` on the `IProduct` is product code for this specific product
   * eg. premium and premium 10 have different `productId` values
   *
   * NOTE: This is called `productCode` on the `INbnProduct`
   */
  productId: string;
  description: string;
  productFamily?: PRODUCT_FAMILY;
}

export interface IMobileProductPlanBase extends IProduct {
  data: IData;
  price: IRecurringCharge;
  availability: PlanAvailability;
}

export interface IMobileProductPlan extends IMobileProductPlanBase {
  addons: IAddon[];
  productFamily: typeof PRODUCT_FAMILY.MOBILE_PLANS;
  productTier?: MobileProductTier;
}

export interface IMobileDataProductPlan extends IMobileProductPlanBase {
  productFamily: typeof PRODUCT_FAMILY.MOBILE_DATA_PLANS;
  productTier?: MobileDataProductTier;
}

export enum PlanAvailability {
  ALL = 'ALL',
  PUBLIC = 'PUBLIC',
  GRANDFATHERED = 'GRANDFATHERED',
  CONDITIONAL = 'CONDITIONAL',
  SIM_RESTRICTED = 'SIM_RESTRICTED'
}

export interface IAnyPlan extends IProduct {
  data?: IData;
  price: IRecurringCharge;
  addons: IAddon[];
  // permitted is used to make plan card unavailable to choose
  permitted?: boolean;
  productFamily?: PRODUCT_FAMILY;
  productTier?: MobileProductTier;
  availability?: PlanAvailability;
}

export interface ITopUp extends IProduct {
  data: IData;
  price: ICharge;
}

export type EntitlementType = 'data' | 'validity' | 'voice';
export interface IIREntitlement {
  type: EntitlementType;
  unit: string;
  value: string;
}
export interface IInternationalRoamingProduct extends IProduct {
  price: ICharge;
  productFamily: PRODUCT_FAMILY;
  entitlements: IIREntitlement[];
}

export interface IDataGift extends IProduct {}

export interface IAddon extends IProduct {
  included: boolean;
  description: string;
  price: IRecurringCharge;
}

export interface INbnPlanSpeed {
  downstreamSpeed: string;
  upstreamSpeed: string;
  typicalEveningDownstreamSpeed: string;
  typicalEveningUpstreamSpeed: string;
  dataSize: string;
  speedUnit: string;
}

export interface INbnModemOption extends IProduct {
  paymentOptions: (IChargeWithDisplayText | IRecurringChargeWithDisplayText)[];
}

export interface INbnNdcOption extends IProduct {
  paymentOptions: (IChargeWithDisplayText | IRecurringChargeWithDisplayText)[];
}

export interface INbnCatalogPlan extends IProduct {
  price: IRecurringChargeWithBaseValue;
  speed: INbnPlanSpeed;
  modemOptions: INbnModemOption[];
  ndcOptions: INbnNdcOption[];
  planProductCode?: string;
  campaignCode?: string;
  active?: boolean;
  replacedBy?: {
    productCode: string;
    price: IRecurringChargeWithDisplayText;
  };
  startDate?: string;
  duration?: number;
  durationUnitOfMeasure?: 'Month';
  /**
   * Separate end dates for plan vs promo
   */
  planEndDate?: string;
  promoEndDate?: string;

  isHighSpeed?: boolean;
  displayLabel?: string;
}

export enum NBN_TECH_TYPE {
  FTTP = 'FTTP',
  FW = 'FW',
  FTTB = 'FTTB',
  HFC = 'HFC',
  FTTC = 'FTTC',
  FTTN = 'FTTN',
  SATELLITE = 'SATELLITE'
}

export const NBN_TECH_TYPE_LABELS: Record<NBN_TECH_TYPE, string> = {
  FTTB: 'Fibre to the Building',
  FTTC: 'Fibre to the Curb',
  FTTN: 'Fibre to the Node',
  FTTP: 'Fibre to the Premises',
  FW: 'Fixed Wireless',
  HFC: 'Hybrid Fibre Coaxial',
  SATELLITE: 'Satellite'
};
